import React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider as CoreThemeProvider, useTheme } from 'react-jss';
import { objToArr, contrast, hexToHsl } from './helpers';
import cx from '../../utils/cx';
import useStyle from './style';

function ThemeProvider({
  children,
  className,
  id,
  cssHelpers,
  colors,
  sizes,
  speed,
  direction,
  defaultIconsDirection,
  defaultIconsViewBox,
  defaultVehiclePlateIconsBaseUrl,
}) {
  const values = {};

  values.colors = (() => {
    const ret = {};
    objToArr({
      ...ThemeProvider.defaultProps.colors,
      ...colors,
    }, (key, _value) => {
      const value = hexToHsl(_value);
      const cover = (v) => `hsl(${v[0]}, ${v[1]}%, ${v[2]}%)`;
      const covera = (v, a) => `hsla(${v[0]}, ${v[1]}%, ${v[2]}%, ${a})`;
      ret[key] = {
        normal: cover(value), // ok
        text: cover(contrast(value, value[2] < 70 ? 75 : -75)), // ok
        background: covera(value, 0.12), // when you want to use 'normal' as color
        shadow: covera(contrast(value, value[2] < 70 ? 100 : -100), 0.1), // ok
        lowestOpacity: covera(value, 0.02), // ok
        lowerOpacity: covera(value, 0.05), // ok
        lowOpacity: covera(value, 0.1), // ok
        highOpacity: covera(value, 0.2), // ok
        hover: cover(contrast(value, value[2] > 80 ? -2 : 2)), // ok, but
        active: cover(contrast(value, value[2] < 20 ? 4 : -4)), // ok, but
        light: cover(contrast(value, 8)),
        dark: cover(contrast(value, -8)),
        placeholder: cover(contrast(value, value[2] < 60 ? 40 : -40)),
        border: cover(contrast(value, value[2] < 20 ? 10 : -10)),
        borderhover: cover(contrast(value, value[2] < 20 ? 30 : -30)),
        contrast: (percetange) => (
          cover(contrast(value, value[2] < 20 ? percetange : (-1 * percetange)))
        ),
      };
    });
    return ret;
  })();

  values.sizes = (() => {
    const ret = {};
    objToArr({
      ...ThemeProvider.defaultProps.sizes,
      ...sizes,
    }, (key, _value) => {
      const value = parseFloat(_value);
      ret[key] = value;
    });
    return ret;
  })();

  values.speed = (() => ({
    fast: speed * 0.4,
    normal: speed,
    slow: speed * 1.7,
  }))();

  const dirStart = direction === 'ltr' ? 'left' : 'right';
  const dirEnd = direction === 'ltr' ? 'right' : 'left';
  values.direction = (() => ({
    start: dirStart,
    end: dirEnd,
    left: 'left',
    right: 'right',
    static: (dynamic = '') => dynamic.replace('start', dirStart).replace('end', dirEnd),
    transformFactor: direction === 'ltr' ? 1 : -1,
    center: 'center',
    value: direction,
  }))();

  values.defaultIconsDirection = defaultIconsDirection;
  values.defaultIconsViewBox = defaultIconsViewBox;
  values.defaultVehiclePlateIconsBaseUrl = defaultVehiclePlateIconsBaseUrl;

  return (
    <CoreThemeProvider theme={values}>
      <Container className={className} id={id} cssHelpers={cssHelpers}>
        {children}
      </Container>
    </CoreThemeProvider>
  );
}
function Container({
  children,
  className,
  id,
  cssHelpers,
}) {
  const theme = useTheme();
  const classes = useStyle({ cssHelpers });

  return (
    <div
      dir={theme.direction.value}
      className={cx(classes.themeProvider, className)}
      id={id}
    >
      {children}
    </div>
  );
}

ThemeProvider.propTypes = {
  cssHelpers: PropTypes.bool,
  /** Object of Hex Colors as String */
  colors: PropTypes.objectOf(PropTypes.string),
  /** Object of Numbers in px */
  sizes: PropTypes.objectOf(PropTypes.number),
  /** Number in milliseconds */
  speed: PropTypes.number,
  direction: PropTypes.oneOf(['ltr', 'rtl']),
  defaultIconsDirection: PropTypes.oneOf(['ltr', 'rtl']),
  defaultIconsViewBox: PropTypes.string,
  defaultVehiclePlateIconsBaseUrl: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
};

ThemeProvider.defaultProps = {
  cssHelpers: true,
  colors: {
    background: '#fff',
    gray: '#a3a3a3',
    text: '#404040',
    primary: '#00d170',
    primary2: '#22a958',
    danger: '#bd272d',
    success: '#00cc6d',
    lightGray: '#e6e6e6',
  },
  sizes: {
    base: 8,
    fontBase: 14,
    radius: 6,
  },
  speed: 250,
  direction: 'ltr',
  defaultIconsDirection: 'rtl',
  defaultIconsViewBox: '0 0 24 24',
  defaultVehiclePlateIconsBaseUrl: 'https://passenger-pwa-cdn.snapp.ir/icons/plate-icons',
  className: '',
  id: '',
};


export default ThemeProvider;
